
import Editor from '@hugerte/hugerte-vue'
import { useHugeRTE } from '../../foundation/js/loadHugeRTE'
import GetLabel from "foundation/js/getLabel"
import emailValidator from "foundation/js/inputValidators/emailValidator";
import isBoolean from "foundation/js/inputValidators/isBoolean";
import nonEmptyStringValidator from "foundation/js/inputValidators/nonEmptyStringValidator";
import phoneValidator from "foundation/js/inputValidators/phoneValidator";
import postalCodeValidator from "foundation/js/inputValidators/postalCodeValidator";
import FormInputTextField from "../FormInputTextField/FormInputTextField"
import FormInputNameField from "../FormInputNameField/FormInputNameField"
import FormAddressField from "../FormAddressField/FormAddressField"
import FormInputEmailField from "../FormInputEmailField/FormInputEmailField"
import FormInputPhoneField from "../FormInputPhoneField/FormInputPhoneField"
import FormCheckbox from "../FormCheckbox/FormCheckbox"
import FormInputDropdown from "components/FormInputDropdown/FormInputDropdown";
import { useStore } from "vuex";
import { ref, computed } from "vue";
import AttachmentsEditor from "../AttachmentsEditor/AttachmentsEditor.vue";
import FormInputTextarea from "components/FormInputTextarea/FormInputTextarea";

export default {
    name: "CreateFrivilligJob",
    components: {
        Editor,
        AttachmentsEditor,
        FormInputTextField,
        FormInputNameField,
        FormAddressField,
        FormInputEmailField,
        FormInputDropdown,
        FormCheckbox,
        FormInputPhoneField,
        FormInputTextarea
    },
    props: {
        subjects: {
            type: Array,
            required: true,
        },
        jobInfo: {
            type: Object,
            required: false,
        },
        isEditContext: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    setup(props) {
        const store = useStore()
        const displayError = ref(false)
        const getLabel = (labelKey) => GetLabel({ labelKey, labelCollection: "CreateFrivilligJob" })

        const submitPath = props.isEditContext ? 'frivilligjob/updateFrivilligjob' : 'frivilligjob/createFrivilligjob'

        const isSubmitting = computed(() => {
            return store.state.frivilligjob.isSubmitting
        })

        const url = `/${process.env.FRIVILLIGJOB_IMAGE_GET.replace("[frivilligjobId]", props.jobInfo?.jobId)}`
        const activeImage = ref({
            "name": "",
            "url": url,
            "size": ""
        })

        const isPeriodValid = ref(true)

        const startDate = ref(props.jobInfo?.startDate ?? "")
        const startTime = ref(props.jobInfo?.startTime ?? "")
        const endDate = ref(props.jobInfo?.endDate ?? "")
        const endTime = ref(props.jobInfo?.endTime ?? "")
        const expirationDate = ref(props.jobInfo?.expirationDate ?? '')
        const images = ref((props.jobInfo?.hasImage ? [activeImage.value] : []) ?? [])
        const hasImageChanged = ref(false)

        const jobData = ref({
            "jobId": props.jobInfo?.jobId,
            "jobTitle": props.jobInfo?.jobTitle,
            "aboutJob": props.jobInfo?.aboutJob,
            "offerDescription": props.jobInfo?.offerDescription,
            "expectationDescription": props.jobInfo?.expectationDescription,
            "placeName": props.jobInfo?.placeName ?? '',
            "address": props.jobInfo?.address ?? '',
            "postalCode": props.jobInfo?.postalCode ?? '',
            "city": props.jobInfo?.city ?? '',
            "isCountrywide": props.jobInfo?.isCountrywide ?? true,
            "isSelfContactPerson": props.jobInfo?.isSelfContactPerson ?? true,
            "contactPersonName": props.jobInfo?.contactPersonName ?? '',
            "contactPersonEmail": props.jobInfo?.contactPersonEmail ?? '',
            "contactPersonPhone": props.jobInfo?.contactPersonPhone ?? '',
            "timeConsumption": props.jobInfo?.timeConsumption,
            "hasSpecificTime": props.jobInfo?.hasSpecificTime ?? false,
        })
        const hasSpecificDate = ref(props.jobInfo?.hasSpecificDate ?? false)
        
        const showSpinner = ref(false)
        const { editorReady } = useHugeRTE()

        const addressValidator = (address, postalCode, city, isCountrywide) => {
            return isCountrywide || (address && postalCodeValidator(postalCode) && city)
        }
        
        const contactInformationValidator = () => {
            return jobData.value.isSelfContactPerson || 
                (nonEmptyStringValidator(jobData.value.contactPersonEmail) && nonEmptyStringValidator(jobData.value.contactPersonName)) 
        }

        const validateFields = () => {
            return validatePeriod(
                    formatDateTime(startDate.value, startTime.value),
                    formatDateTime(endDate.value, endTime.value))
                && nonEmptyStringValidator(jobData.value.jobTitle)
                && nonEmptyStringValidator(jobData.value.aboutJob)
                && nonEmptyStringValidator(jobData.value.offerDescription)
                && nonEmptyStringValidator(jobData.value.expectationDescription)
                && addressValidator(jobData.value.address, jobData.value.postalCode, jobData.value.city, jobData.value.isCountrywide)
                && nonEmptyStringValidator(jobData.value.timeConsumption)
                && contactInformationValidator()
        }

        const setIsCountryWide = (e) => {
            jobData.value.isCountrywide = e.target.checked

            if (jobData.value.isCountrywide){
                jobData.value.address = '',
                jobData.value.city = '',
                jobData.value.postalCode = ''
            }
        }

        const setSpecificDateCheckbox = (e) => {
            hasSpecificDate.value = e.target.checked
            
            if (!hasSpecificDate.value){
                startDate.value = ''
                endDate.value = ''
                startTime.value = ''
                endTime.value = ''

                jobData.value.hasSpecificTime = false
            }
        }

        function isDateValid(dateStr) {
            return !isNaN(new Date(dateStr))
          }

        const validatePeriod = (startDate, endDate) => {
            if (!isDateValid(startDate) || !isDateValid(endDate)){
                return true
            }


            if (startDate > endDate){
                return false
            }

            return true
        }

        const setSpecificTimeCheckbox = (e) => {
            jobData.value.hasSpecificTime = e.target.checked
            
            if (!jobData.value.hasSpecificTime){
                startTime.value = ''
                endTime.value = ''
            }
        }

        const handleTimeConsumptionSelector = (e) => {
            jobData.value.timeConsumption = e.target.value
        }

        const formatDateTime = (date, time) => {
            let timeString = time || "12:00"
            let combinedDatetime = `${date}T${timeString}:00`

            return new Date(combinedDatetime);
        }

        const createRequest = () => {
            return {
                ...jobData.value,
                "startDate": formatDateTime(startDate.value, startTime.value),
                "endDate": formatDateTime(endDate.value, endTime.value),
                "expirationDate": expirationDate.value,
                "subjects": props.subjects
                    .filter(subject => subject.isSelected === true)
                    .map(subject => subject.value),
            }
        }

        const setImage = (e) => {
            hasImageChanged.value = true
            images.value = e
        }

        const submitCreateJob = () => {
            displayError.value = true

            if (validateFields()){
                let request = createRequest()

                store.dispatch(submitPath, request)
                .then(async result => {
                    if (!result.continueUrl){
                        displayError.value = true

                        return
                    }

                    if (!hasImageChanged.value){
                        window.location.href = result.continueUrl

                        return
                    }

                    if (images.value.length == 1){
                        const formData = new FormData()
                        formData.append('image', images.value[0])
        
                        await store.dispatch('frivilligjob/submitFrivilligjobImage', {
                            id: result.jobId,
                            body: formData
                        })
                    }
                    else {
                        await store.dispatch('frivilligjob/removeFrivilligjobImage', result.jobId)
                    }

                    window.location.href = result.continueUrl
                })
            }
            else {
                displayError.value = true
            }
        }


        const isFutureDate = (expirationDate) => {

            if (!expirationDate) return false
            
            var now = new Date()
            var startTimestamp = new Date(`${expirationDate}`)
            
            return now <= startTimestamp
        }

        const setSubject = (id, isSelected) => props.subjects.find((subject) => subject.value === id).isSelected = isSelected

        return {
            jobData,
            showSpinner,
            displayError,
            isSubmitting,
            hasSpecificDate,
            startDate,
            endDate,
            startTime,
            endTime,
            isPeriodValid,
            expirationDate,
            images,
            getLabel,
            emailValidator,
            isBoolean,
            nonEmptyStringValidator,
            phoneValidator,
            postalCodeValidator,
            addressValidator,
            isFutureDate,
            submitCreateJob,
            setSubject,
            setSpecificDateCheckbox,
            setSpecificTimeCheckbox,
            handleTimeConsumptionSelector,
            setImage,
            setIsCountryWide,
            editorReady
        }
    }
}